(() => {
  const selectors = {
    section: '[data-our-process-section]',
    viewport: '[data-our-process-viewport]',
    track: '[data-our-process-track]',
    card: '.our-process__card'
  };

  const ANIMATED_CLASS = 'our-process--scroll-animation';

  const state = new WeakMap();

  const getGsapRuntime = () => {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger || window.gsap?.plugins?.ScrollTrigger;

    if (!gsap || !ScrollTrigger) return null;
    return { gsap, ScrollTrigger };
  };

  const getTotalScroll = (track, viewport) => Math.max(0, track.scrollWidth - viewport.clientWidth);

  const destroyProcessSection = (section) => {
    const teardown = state.get(section);
    if (typeof teardown === 'function') teardown();
    state.delete(section);
  };

  const initProcessSection = (section, runtime) => {
    destroyProcessSection(section);

    const { gsap } = runtime;
    const viewport = section.querySelector(selectors.viewport);
    const track = section.querySelector(selectors.track);

    if (!viewport || !track) return;

    const cards = track.querySelectorAll(selectors.card);
    if (cards.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
      const totalScroll = getTotalScroll(track, viewport);
      if (totalScroll <= 0) return undefined;

      section.classList.add(ANIMATED_CLASS);

      const tween = gsap.to(track, {
        x: () => -getTotalScroll(track, viewport),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'center center',
          end: () => `+=${Math.max(1, getTotalScroll(track, viewport))}`,
          pin: true,
          pinSpacing: true,
          scrub: 0.25,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        }
      });

      return () => {
        section.classList.remove(ANIMATED_CLASS);
        tween.scrollTrigger?.kill();
        tween.kill();
        gsap.set(track, { clearProps: 'transform' });
      };
    });

    state.set(section, () => {
      section.classList.remove(ANIMATED_CLASS);
      mm.revert();
      gsap.set(track, { clearProps: 'transform' });
    });
  };

  const arrowState = new WeakMap();

  const destroyMobileArrows = (section) => {
    const teardown = arrowState.get(section);
    if (typeof teardown === 'function') teardown();
    arrowState.delete(section);
  };

  const initMobileArrows = (section) => {
    destroyMobileArrows(section);

    const viewport = section.querySelector(selectors.viewport);
    const track = section.querySelector(selectors.track);
    const prevBtn = section.querySelector('[data-sourcing-scroll="prev"]');
    const nextBtn = section.querySelector('[data-sourcing-scroll="next"]');

    if (!viewport || !track || (!prevBtn && !nextBtn)) return;

    const updateButtons = () => {
      if (window.innerWidth >= 768) return;
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const canScroll = maxScrollLeft > 2;

      const isAtStart = !canScroll || viewport.scrollLeft <= 5;
      const isAtEnd = !canScroll || viewport.scrollLeft >= maxScrollLeft - 5;

      if (prevBtn) {
        prevBtn.classList.toggle('is-disabled', isAtStart);
        prevBtn.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.classList.toggle('is-disabled', isAtEnd);
        nextBtn.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
      }
    };

    const getTargetScrollLeft = (direction) => {
      const currentScroll = viewport.scrollLeft;
      const currentCards = Array.from(track.querySelectorAll(selectors.card));
      if (!currentCards.length) return currentScroll + direction * (viewport.clientWidth * 0.85);

      if (direction > 0) {
        const nextCard = currentCards.find((card) => card.offsetLeft > currentScroll + 12);
        if (nextCard) return nextCard.offsetLeft;
        return viewport.scrollWidth - viewport.clientWidth;
      } else {
        const prevCards = currentCards.filter((card) => card.offsetLeft < currentScroll - 12);
        if (prevCards.length) {
          const prevCard = prevCards[prevCards.length - 1];
          return Math.max(0, prevCard.offsetLeft);
        }
        return 0;
      }
    };

    const onArrowClick = (direction) => {
      if (window.innerWidth >= 768) return;
      const targetLeft = getTargetScrollLeft(direction);
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      viewport.scrollTo({
        left: targetLeft,
        behavior: prefersReduced ? 'auto' : 'smooth'
      });
    };

    const onPrevClick = (e) => {
      e.preventDefault();
      onArrowClick(-1);
    };

    const onNextClick = (e) => {
      e.preventDefault();
      onArrowClick(1);
    };

    if (prevBtn) prevBtn.addEventListener('click', onPrevClick);
    if (nextBtn) nextBtn.addEventListener('click', onNextClick);

    const onScroll = () => window.requestAnimationFrame(updateButtons);
    const onResize = () => window.requestAnimationFrame(updateButtons);

    viewport.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    updateButtons();

    arrowState.set(section, () => {
      if (prevBtn) prevBtn.removeEventListener('click', onPrevClick);
      if (nextBtn) nextBtn.removeEventListener('click', onNextClick);
      viewport.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    });
  };

  const onReady = () => {
    document.querySelectorAll(selectors.section).forEach((section) => {
      initMobileArrows(section);
    });

    const runtime = getGsapRuntime();
    if (runtime) {
      const { gsap, ScrollTrigger } = runtime;
      gsap.registerPlugin(ScrollTrigger);

      document.querySelectorAll(selectors.section).forEach((section) => {
        initProcessSection(section, runtime);
      });
    }

    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target?.matches?.(selectors.section)
        ? event.target
        : event.target?.querySelector?.(selectors.section);

      if (section) {
        initMobileArrows(section);
        if (runtime) {
          initProcessSection(section, runtime);
          runtime.ScrollTrigger?.refresh();
        }
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target?.matches?.(selectors.section)
        ? event.target
        : event.target?.querySelector?.(selectors.section);

      if (section) {
        destroyMobileArrows(section);
        destroyProcessSection(section);
        runtime?.ScrollTrigger?.refresh();
      }
    });

    window.addEventListener('load', () => {
      runtime?.ScrollTrigger?.refresh();
      document.querySelectorAll(selectors.section).forEach((section) => {
        initMobileArrows(section);
      });
    }, { once: true });

    runtime?.ScrollTrigger?.refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
